import axios from 'axios';
import { resetKewaContact } from 'kewa-tracker-web';
import qs from 'query-string';
import { toast } from 'react-toastify';
import { APP_EVENTS } from '../Constants/Constants';
import { publish } from '../Events/AppEvents';
import errorExtractor from '../Utils/ErrorExtractor';

export const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;
export const API_TRACKING_BASE_URL = process.env.REACT_APP_TRACKING_API_ENDPOINT;

// Configure axios globally
axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(params, { arrayFormat: 'repeat' });
};

// New axios feature: Create an axios instance for better control
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // Default timeout
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authenticate_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle session expiry in interceptor
    if (error?.response?.data?.detail?.includes('Jwt expired')) {
      toast.error('Session expired, please login again');
      // Reset Kewa contact on JWT expiry
      resetKewaContact().catch((err) => {
        console.error('Failed to reset Kewa contact:', err);
      });
      localStorage.removeItem('authenticate_token');
      window.location.reload();
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

const API = {
  get: (url, option, query, successCallback, errorCallback, requestHeader = {}) =>
    request({
      url,
      init: {
        method: 'GET',
        headers: requestHeader,
      },
      query,
      option,
      successCallback,
      errorCallback,
    }),
  post: (url, payload, option, query, successCallback, errorCallback, requestHeader = {}) =>
    request({
      url,
      init: {
        method: 'POST',
        headers: requestHeader,
      },
      query,
      payload,
      option,
      successCallback,
      errorCallback,
    }),
  put: (url, option, query, successCallback, errorCallback, requestHeader = {}) =>
    request({
      url,
      init: {
        method: 'PUT',
        headers: requestHeader,
      },
      query,
      option,
      successCallback,
      errorCallback,
    }),
  patch: (url, option, query, successCallback, errorCallback, requestHeader = {}) =>
    request({
      url,
      init: {
        method: 'PATCH',
        headers: requestHeader,
      },
      query,
      option,
      successCallback,
      errorCallback,
    }),
  delete: (url, option, query, successCallback, errorCallback, requestHeader = {}) =>
    request({
      url,
      init: {
        method: 'DELETE',
        headers: requestHeader,
      },
      query,
      option,
      successCallback,
      errorCallback,
    }),
};

/**
 * Build headers dynamically for each API request
 * @param headers
 * @return {{Authorization: string, Accept: string, "Content-Type": string}}
 */
function buildHeaders(headers, requestHeader) {
  const token = localStorage.getItem('authenticate_token');
  const appCode = 'EPR005';
  const newHeader = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    appCode: appCode,
    ...headers,
    ...requestHeader,
  };

  if (token) {
    newHeader.Authorization = 'Bearer ' + token;
  }

  return newHeader;
}

/**
 * Generic request handler
 * @param props
 * @return {Promise<AxiosResponse<any>>}
 */
function request(props) {
  const { url, init, query, payload, option, successCallback, errorCallback, requestHeader } =
    props;
  const fetchUrl = /^http/.test(url) ? url : `${API_BASE_URL}${url}`;
  return axiosInstance({
    url: fetchUrl,
    method: init.method,
    data: payload ?? option,
    params: query,
    headers: buildHeaders(init.headers, requestHeader),
    timeout: option && option.timeout ? option.timeout : 300000,
  })
    .then((response) => {
      successCallback && successCallback(response);
      return response;
    })
    .catch((error) => {
      if (error?.response?.data?.detail?.includes('Jwt expired')) {
        toast.error('Session expired, please login again');
        // Reset Kewa contact on JWT expiry
        resetKewaContact().catch((err) => {
          console.error('Failed to reset Kewa contact:', err);
        });
        localStorage.removeItem('authenticate_token');
        window.location.reload();
      }

      errorCallback && errorCallback(error);
      // Ignore global error handling if specified
      if (option && option.ignoreError) return;
      onError(error);
    });
}

/**
 * Global error handler for API failures
 * @param error
 */
let onError = (error) => {
  // Prevent handling if already handled
  if (error?.handled) return;

  if (error?.message === 'Network Error' || (error?.response && error?.response.status === 503)) {
    publish(
      APP_EVENTS.topic.SERVER_ERROR,
      APP_EVENTS.event.SERVER_DOWN_503,
      error?.response ?? error
    );
    error.handled = true;
    return;
  }
  if (error?.response) {
    if (error?.response.status === 0) {
      toast.warn('Please check if you are connected to Internet');
    } else if (error?.response.status === 400) {
      const err = errorExtractor(error?.response);
      if (!!err?.message && !!err?.customMessage) {
        toast.warn(
          <>
            {err.message}
            <br />
            {err.customMessage}
          </>
        );
      } else if (!!err?.customMessage) {
        toast.warn(err.customMessage);
      } else if (!!err?.message) {
        toast.warn(err.message);
      }
    } else if (error?.response.status === 500) {
      const err = errorExtractor(error?.response);
      if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error('Server Error');
      }
    } else if (error?.response.status === 401) {
      toast.error('Unauthorized access, please login again');
      // Reset Kewa contact on 401 error
      resetKewaContact().catch((err) => {
        console.error('Failed to reset Kewa contact:', err);
      });
      localStorage.removeItem('authenticate_token');
      window.location.reload();
    } else if (error?.response.status === 403) {
      toast.error('You do not have permission to access this resource');
    } else if (error?.response.status === 404) {
      toast.error('Resource not found');
    } else {
      const err = errorExtractor(error?.response);
      if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error('An unexpected error occurred');
      }
    }
    error.handled = true;
  }
  throw error;
};

export default API;
